import { config } from '@iheartjane/config';
import { trackError } from '@iheartjane/tracking';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMonerisPreload } from '@jane/shared-ecomm/data-access';
import { useCheckout } from '@jane/shared-ecomm/providers';

import { useCheckoutPayments } from '../CheckoutPaymentsProvider';

interface MonerisCallbacks {
  cancel_transaction: () => void;
  error_event: (error: { response_code: string }) => void;
  page_loaded: () => void;
  payment_complete: () => void;
  payment_receipt: () => void;
}

interface MonerisCheckout {
  closeCheckout: (ticket: string) => void;
  setCallback: <TEvent extends keyof MonerisCallbacks>(
    event: TEvent,
    callback: MonerisCallbacks[TEvent]
  ) => void;
  setCheckoutDiv: (
    /** Id of the element the widget mounts its iframe into. */
    divId: string
  ) => void;
  setMode: (mode: 'prod' | 'qa') => void;
  startCheckout: (ticket: string) => void;
}

declare global {
  interface Window {
    /** @see https://legacy-developer.moneris.com/en/Documentation/NA/E-Commerce%20Solutions/Hosted%20Solutions/Moneris%20Checkout */
    monerisCheckout?: new () => MonerisCheckout;
  }
}

interface UseMonerisProps {
  onCancel?: () => void;
  onError?: () => void;
  onLoad?: () => void;
  onSuccess?: (ticket: string) => void;
}

export const MONERIS_WIDGET_DIV_ID = 'monerisCheckout';

export const useMoneris = ({
  onCancel,
  onError,
  onLoad,
  onSuccess,
}: UseMonerisProps) => {
  const [loaded, setLoaded] = useState(false);
  const {
    cartLineItems: { total },
    dispatches: { checkoutError },
    store: { id },
  } = useCheckout();
  const { updatePrepayments } = useCheckoutPayments();
  const { data: monerisPreloadData, error } = useMonerisPreload({
    storeId: String(id),
    amount: total.toFixed(2).toString(),
  });

  useEffect(() => {
    if (error) checkoutError('Could not authorize Moneris');
  }, [error]);

  const widgetRef = useRef<MonerisCheckout>(undefined);

  const handleSuccess = () => {
    onSuccess && onSuccess(monerisPreloadData?.ticket || '');
  };

  const handleError = (e: { response_code: string }) => {
    updatePrepayments({ monerisTicket: '' });
    onError && onError();
    checkoutError(
      'Unable to charge credit or debit card. Please try another payment option.'
    );
    trackError(new Error(`Moneris Payments Error, code: ${e.response_code}`));
  };

  const handleCancel = () => {
    updatePrepayments({ monerisTicket: '' });
    document.body.style.overflow = 'unset';
    onCancel && onCancel();
  };

  const handleLoad = () => {
    setLoaded(true);
    onLoad && onLoad();
  };

  useEffect(() => {
    if (monerisPreloadData?.ticket) {
      if (!window.monerisCheckout) {
        trackError(
          new Error('Moneris Web Payments SDK failed to load properly')
        );
      } else {
        const monerisPay = new window.monerisCheckout();
        monerisPay.setMode(config.monerisEnv);
        monerisPay.setCheckoutDiv(MONERIS_WIDGET_DIV_ID);
        monerisPay.setCallback('page_loaded', handleLoad);
        monerisPay.setCallback('cancel_transaction', handleCancel);
        monerisPay.setCallback('error_event', handleError);
        monerisPay.setCallback('payment_receipt', handleSuccess);
        monerisPay.setCallback('payment_complete', handleSuccess);
        widgetRef.current = monerisPay;
        setLoaded(true);
      }
    }
  }, [monerisPreloadData?.ticket]);

  const handleOpenWidget = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => {
      if (loaded && monerisPreloadData?.ticket) {
        document.body.style.overflow = 'hidden';
        widgetRef.current?.startCheckout(monerisPreloadData.ticket);
      }
    },
    [loaded, monerisPreloadData?.ticket]
  );

  const handleCloseWidget = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => {
      if (loaded && monerisPreloadData?.ticket) {
        document.body.style.overflow = 'unset';
        widgetRef.current?.closeCheckout(monerisPreloadData.ticket);
      }
    },
    [loaded, monerisPreloadData?.ticket]
  );

  return {
    closeWidget: handleCloseWidget,
    openWidget: handleOpenWidget,
    widgetLoaded: loaded,
  };
};
